import React from 'react';
import { APITypes } from '../../../primary-record-types';
import { View } from 'react-native';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { getIntl } from '../../../utils/intl';
import { PRScrollView } from '../Scroll';
import SearchResult from './SearchResult';
import Text from '../../v2BaseComponents/Text';
import Stack from '../../v2BaseComponents/Stack';
import { isSearchEnabled } from '../../../hooks/api/useMedicalProfileSearch';
import EmptyStateHint from '../EmptyStateHint';
import SearchHistory from './SearchHistory';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import PressableDynamicOpacity from '../../v2BaseComponents/PressableDynamicOpacity';
import { hexToRgbA } from '../../../utils/hexToRGBA';
import { colors } from '../../../utils/theme';
import Icon from '../../v2BaseComponents/Icon';

const intl = getIntl('SearchResults', {
    searching: 'Searching "{searchText}"...',
    noResults: 'No results found for "{searchText}"',
    searchProfile: 'Search across your medical profile',
    bestMatches: 'Best Matches',
    actions: 'Actions',
    askTheChatAssistant: 'Ask the chat assistant "{text}"',
});

export type SearchResultsOwnProps = {
    isLoadingSearch: boolean;
    searchResults: APITypes.chatService.PerformGeneralMedicalProfileSearchResponse;
    searchText: string;
    medicalProfileId: string;
    onSelectedResult: () => void;
    historicalSearches: string[];
    onSetSearchText: (searchText: string) => void;
    onPromptChat: (prompt?: string) => void;
};

export type SearchResultsProps = SearchResultsOwnProps;

const SearchResults: React.FC<SearchResultsProps> = (props) => {
    const renderContents = () => {
        const renderMessage = (message: string) => (
            <View
                style={{
                    flex: 1,
                    alignSelf: 'stretch',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: mainContentPadding,
                }}
            >
                <Text variant="bold-h6" color="Gray-Text">
                    {message}
                </Text>
            </View>
        );

        if (!isSearchEnabled(props.searchText) && !props.historicalSearches?.length) {
            return (
                <View
                    style={{
                        flex: 1,
                        alignSelf: 'stretch',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: mainContentPadding,
                    }}
                    testID="empty-profile-search"
                >
                    <View>
                        <EmptyStateHint variant="empty-profile-search" />
                    </View>
                </View>
            );
        } else if (!isSearchEnabled(props.searchText)) {
            return (
                <SearchHistory
                    onSetSearchText={props.onSetSearchText}
                    searchHistory={props.historicalSearches}
                    onPromptChat={() => props.onPromptChat()}
                />
            );
        } else if (!props.searchResults?.searchResults?.length) {
            return renderMessage(intl('searching', { searchText: props.searchText }));
        } else {
            return (
                <PRScrollView
                    style={{
                        flex: 1,
                        alignSelf: 'stretch',
                    }}
                >
                    <Stack vertical style={{ alignSelf: 'stretch' }}>
                        <Text
                            variant="bold-body-1"
                            color="Primary-Reg"
                            style={{ paddingLeft: mainContentPadding, marginBottom: 8 }}
                        >
                            {intl('actions')}
                        </Text>
                        <PressableDynamicOpacity
                            onPress={() => props.onPromptChat(props.searchText)}
                            testID="prompt-chat-with-search-text"
                            style={{
                                flex: 1,
                                borderRadius: 4,
                                marginBottom: 20,
                            }}
                            addHoverEffect
                            customStateStyles={{
                                hovered: {
                                    backgroundColor: hexToRgbA(colors['Grey-Light'], 0.2),
                                },
                                pressed: {
                                    backgroundColor: hexToRgbA(colors['Grey-Light'], 0.5),
                                },
                            }}
                            hoverDetectionContainerStyles={{
                                alignSelf: 'stretch',
                            }}
                        >
                            <Stack horizontal align="center" justify="flex-start" space={2}>
                                <Text
                                    variant="bold-h5"
                                    wrapText
                                    style={{ padding: 4, paddingLeft: mainContentPadding * 1.2 }}
                                >
                                    {intl('askTheChatAssistant', { text: props.searchText })}
                                </Text>
                                <Icon name="AISparkle" size="minimal-button-icon" />
                            </Stack>
                        </PressableDynamicOpacity>
                        <Text
                            variant="bold-body-1"
                            color="Primary-Reg"
                            style={{ paddingLeft: mainContentPadding, marginBottom: 8 }}
                        >
                            {intl('bestMatches')}
                        </Text>
                        {(props.searchResults?.searchResults || [])?.map((result, index) => {
                            return (
                                <SearchResult
                                    key={`${result.category}-${index}`}
                                    searchResult={result}
                                    onSelectedResult={props.onSelectedResult}
                                    medicalProfileId={props.medicalProfileId}
                                    searchText={props.searchText}
                                />
                            );
                        })}
                    </Stack>
                </PRScrollView>
            );
        }
    };

    return (
        <View
            style={{
                flex: 1,
                alignSelf: 'stretch',
            }}
        >
            <AnimateLoadedContent
                isLoading={props.isLoadingSearch}
                message={intl('searching', {
                    searchText: props.searchText,
                })}
            >
                {renderContents}
            </AnimateLoadedContent>
        </View>
    );
};

export default React.memo(SearchResults);
